import React from "react";
import { notification } from "@findemdev/maui";

const APP_VERSION_WORKER_PATH = "/workers/appVersion.worker.js";
const APP_UPDATE_NOTIFICATION_KEY = "app-update-notification";
const APP_VERSION_MISMATCH_MESSAGE = "findem:app-version-mismatch";
const APP_VERSION_MISMATCH_ACK_MESSAGE = "findem:app-version-mismatch:ack";
const APP_VERSION_MISMATCH_RESET_MESSAGE =
  "findem:app-version-mismatch:reset";

interface UseCheckAppVersionOptions {
  enabled?: boolean;
}

interface AppVersionMismatchMessage {
  key?: string;
  type: string;
  version?: string;
}

interface IsTrustedIframeOriginParams {
  url: string;
}

interface AppVersionMismatchMessageParams {
  data: unknown;
}

interface IframeResetTarget {
  origin: string;
  source: Window;
}

const getCurrentAppVersion = () => {
  if (typeof APP_VERSION_WITH_ID === "string") {
    return APP_VERSION_WITH_ID;
  }

  return undefined;
};

const isTrustedIframeOrigin = ({ url }: IsTrustedIframeOriginParams) => {
  const lowerUrl = url.toLowerCase();

  return lowerUrl.includes("rra") || lowerUrl.includes("exec");
};

const isAppVersionMismatchMessage = (
  params: AppVersionMismatchMessageParams,
): params is AppVersionMismatchMessageParams & {
  data: AppVersionMismatchMessage;
} => {
  const { data } = params;

  if (typeof data !== "object" || data === null) {
    return false;
  }

  const message = data as AppVersionMismatchMessage;

  return (
    message.key === APP_UPDATE_NOTIFICATION_KEY &&
    message.type === APP_VERSION_MISMATCH_MESSAGE
  );
};

const useCheckAppVersion = ({
  enabled = true,
}: UseCheckAppVersionOptions = {}) => {
  const [pollingResetKey, setPollingResetKey] = React.useState(0);
  const isNotificationShowing = React.useRef(false);
  const iframeResetTargets = React.useRef<IframeResetTarget[]>([]);
  const shouldCheckAppVersion =
    enabled && import.meta.env.MODE !== "development";

  const addIframeResetTarget = React.useCallback(
    ({ origin, source }: IframeResetTarget) => {
      const hasTarget = iframeResetTargets.current.some(
        (target) => target.origin === origin && target.source === source,
      );

      if (!hasTarget) {
        iframeResetTargets.current.push({ origin, source });
      }
    },
    [],
  );

  const resetNotificationState = React.useCallback(() => {
    const wasNotificationShowing = isNotificationShowing.current;

    isNotificationShowing.current = false;
    iframeResetTargets.current.forEach(({ origin, source }) => {
      source.postMessage(
        {
          key: APP_UPDATE_NOTIFICATION_KEY,
          type: APP_VERSION_MISMATCH_RESET_MESSAGE,
        },
        origin,
      );
    });
    iframeResetTargets.current = [];

    if (wasNotificationShowing) {
      setPollingResetKey((currentKey) => currentKey + 1);
    }
  }, []);

  const showUpdateNotification = React.useCallback(() => {
    if (isNotificationShowing.current) {
      return false;
    }

    isNotificationShowing.current = true;
    notification.info({
      message: "New app version available",
      actionText: "Reload",
      description: "Please reload the page to get the latest updates",
      key: APP_UPDATE_NOTIFICATION_KEY,
      duration: 0,
      onAction: () => {
        resetNotificationState();
        window.location.reload();
      },
      onClose: resetNotificationState,
    });

    return true;
  }, [resetNotificationState]);

  React.useEffect(() => {
    const currentAppVersion = getCurrentAppVersion();

    if (
      !shouldCheckAppVersion ||
      typeof Worker === "undefined" ||
      !currentAppVersion
    ) {
      return;
    }

    const worker = new Worker(APP_VERSION_WORKER_PATH);
    worker.postMessage({
      appVersion: currentAppVersion,
      startPolling: true,
    });

    worker.onmessage = (event) => {
      const { type, payload } = event.data;

      if (type === "versionMismatch" && showUpdateNotification()) {
        console.warn(`Version mismatch detected: ${payload}`);
      }

      if (type === "error") {
        console.error(`Worker Error: ${payload}`);
      }
    };

    return () => {
      worker.postMessage({ stopPolling: true });
      worker.terminate();
      isNotificationShowing.current = false;
    };
  }, [pollingResetKey, shouldCheckAppVersion, showUpdateNotification]);

  React.useEffect(() => {
    if (!shouldCheckAppVersion) {
      return;
    }

    const handleIframeVersionMessage = (event: MessageEvent<unknown>) => {
      const messageParams = { data: event.data };

      if (
        !isTrustedIframeOrigin({ url: event.origin }) ||
        !isAppVersionMismatchMessage(messageParams)
      ) {
        return;
      }

      const source = event.source as Window | null;

      if (source) {
        addIframeResetTarget({ origin: event.origin, source });
      }

      if (showUpdateNotification()) {
        console.warn(
          `Version mismatch detected in embedded RRA app: ${
            messageParams.data.version || "unknown"
          }`,
        );
      }

      source?.postMessage(
        {
          key: APP_UPDATE_NOTIFICATION_KEY,
          type: APP_VERSION_MISMATCH_ACK_MESSAGE,
        },
        event.origin,
      );
    };

    window.addEventListener("message", handleIframeVersionMessage);

    return () => {
      window.removeEventListener("message", handleIframeVersionMessage);
    };
  }, [addIframeResetTarget, shouldCheckAppVersion, showUpdateNotification]);
};

export default useCheckAppVersion;
